import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import ExportButton from "@/components/ExportButton";
import DynamicCustomFields from "@/components/DynamicCustomFields";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users2, Plus } from "lucide-react";

const EMPTY = { company_id: "", full_name: "", email: "", phone: "", customer_company: "", customer_id: "", custom_fields: {} };

export default function Contacts() {
  const { activeCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const params = { ...(activeCompanyId ? { company_id: activeCompanyId } : {}), ...(q ? { q } : {}) };
    const [c, co] = await Promise.all([
      api.get("/contacts", { params }),
      api.get("/companies"),
    ]);
    setItems(c.data);
    setCompanies(co.data);
    if (!form.company_id) setForm((f) => ({ ...f, company_id: activeCompanyId || co.data[0]?.id || "" }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId, q]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contacts", form);
      toast.success("Contact saved");
      setOpen(false);
      setForm({ ...EMPTY, company_id: form.company_id });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="contacts-page">
      <PageHeader
        title="Contacts"
        subtitle="Every sender becomes a searchable CRM contact scoped to the company."
        actions={
          <div className="flex items-center gap-2">
            <ExportButton dataset="contacts" companyId={activeCompanyId} />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" data-testid="new-contact-button"><Plus className="h-4 w-4 mr-1.5" /> New contact</Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">New contact</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="new-contact-form">
                  <F label="Company">
                    <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v, custom_fields: {} })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Full name"><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rounded-xl" data-testid="contact-name-input" /></F>
                    <F label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="rounded-xl" data-testid="contact-email-input" /></F>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl" data-testid="contact-phone-input" /></F>
                    <F label="Customer company"><Input value={form.customer_company} onChange={(e) => setForm({ ...form, customer_company: e.target.value })} className="rounded-xl" data-testid="contact-cc-input" /></F>
                  </div>
                  <DynamicCustomFields companyId={form.company_id} value={form.custom_fields} onChange={(v) => setForm({ ...form, custom_fields: v })} />
                  <DialogFooter><Button type="submit" className="rounded-xl" data-testid="contact-create-submit">Save</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      <div className="p-6 lg:p-8 space-y-3">
        <Input placeholder="Search contacts…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm rounded-sm" data-testid="contacts-search" />
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="tbl w-full" data-testid="contacts-table">
            <thead><tr><th>Contact</th><th>Company</th><th>Conversations</th><th>Last contact</th></tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} data-testid={`contact-${c.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-sm bg-primary/10 text-primary grid place-items-center"><Users2 className="h-3.5 w-3.5" /></div>
                      <div>
                        <div className="font-medium">{c.full_name || c.email}</div>
                        <div className="text-[11px] font-mono text-muted-foreground">{c.email}</div>
                        {c.custom_fields && Object.keys(c.custom_fields).length > 0 && (
                          <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                            {Object.entries(c.custom_fields).slice(0, 3).map(([k, v]) => <span key={k} className="mr-2">{k}: {String(v)}</span>)}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="text-muted-foreground">{c.customer_company || "—"}</td>
                  <td className="font-mono text-xs">{c.total_conversations || 0}</td>
                  <td className="font-mono text-xs text-muted-foreground">{c.last_contact_at?.slice(0, 16).replace("T", " ") || "—"}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="4" className="text-center text-muted-foreground py-8">No contacts yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
